<template>
  <div v-if="inspectionSection">
    <router-link
      :to="{
        name: 'EditInspectionTemplate',
        params: { _id: projectId, inspectionTemplateId: inspectionTemplate._id },
      }"
    >
      <div class="template-menu-contents">
        <h3><locale-text prefix="&lt;&lt; " :text="inspectionTemplate.title" /></h3>
      </div>
    </router-link>
    <div class="edit-inspection-section">
      <div class="boon-form-container" :class="{ archived: inspectionSection.archived }">
        <icon-selector class="h2" :value="inspectionSection.icon" @input="updateProperty('icon', $event)" />
        <translated-field
          ref="title"
          :label="$t('Section Title')"
          :value="inspectionSection.title"
          :readonly="inspectionSection.archived"
          class="half mx-4 my-2"
          @input="updateProperty('title', $event)"
        />

        <iconToggle
          v-if="!inspectionSection.new"
          :value="inspectionSection.archived"
          @input="updateProperty('archived', $event)"
          iconOff="static/icon/H2BOON-list-missing.png"
          iconOn="static/icon/H2BOON-list-plus.png"
          class="right"
          :tab="false"
        />
      </div>
      <div class="items" v-if="!inspectionSection.new">
        <draggable :value="sectionItems" handle=".glyphicon-move" @update="onItemMoved">
          <edit-inspection-template-section-item
            v-for="(item, index) in sectionItems"
            :key="item._id"
            :template="inspectionTemplate"
            :section-id="inspectionSectionId"
            :section-item="item"
            :index="index"
          />
          <edit-inspection-template-section-item
            ref="new"
            :key="`new-section-item-${itemCount}`"
            :template="inspectionTemplate"
            :section-id="inspectionSectionId"
            :index="itemCount"
          />
        </draggable>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import draggable from 'vuedraggable';

import TranslatedField from '@/components/form/TranslatedField';
import inputField from '@/components/form/inputField';
import iconToggle from '@/components/iconToggle/iconToggle';
import IconSelector from '@/components/IconSelector';
import EditInspectionTemplateSectionItem from './EditInspectionTemplateSectionItem';
import LocaleText from '@/components/common/LocaleText';

import getRef from '@/utils/getRef';
import getTranslatedString from '@/utils/getTranslatedString';

export default {
  name: 'EditInspectionSection',

  components: {
    EditInspectionTemplateSectionItem,
    iconToggle,
    IconSelector,
    inputField,
    draggable,
    TranslatedField,
    LocaleText,
  },

  metaInfo() {
    return {
      title: this.title,
    };
  },

  props: {
    projectId: {
      type: String,
      required: true,
    },
    inspectionTemplateId: {
      type: String,
      required: true,
    },
    inspectionSectionId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      inspectionTemplate: {},
      newItemAdded: false,
    };
  },

  computed: {
    ...mapGetters(['currentProjectInspectionTemplatesById']),

    title() {
      if (!this.inspectionTemplate || !this.inspectionSection) {
        return '';
      }

      return this.$t('Edit Inspection Template {title} / {sectionTitle}', {
        title: getTranslatedString(this.inspectionTemplate.title),
        sectionTitle: getTranslatedString(this.inspectionSection.title),
      });
    },

    inspectionSection() {
      const newPlaceHolder = { new: true, title: { sv: '', en: '' } };
      if (!this.inspectionTemplate || !this.inspectionTemplate.sections) {
        return newPlaceHolder;
      }
      return this.inspectionTemplate.sections.find(({ _id }) => _id === this.inspectionSectionId) || newPlaceHolder;
    },

    sectionItems() {
      return this.inspectionSection.new || !this.inspectionSection.items ? [] : this.inspectionSection.items;
    },

    itemCount() {
      return this.sectionItems.length;
    },
  },

  mounted() {
    this.inspectionTemplate = this.currentProjectInspectionTemplatesById(this.inspectionTemplateId);
    this.$nextTick(() => {
      this.focus();
    });
  },

  methods: {
    ...mapActions(['saveInspectionSectionProperties', 'saveInspectionSectionItemProperties']),

    updateProperty(property, newValue) {
      if (this.inspectionSection[property] !== newValue) {
        let newInspectionSectionProperties = {};
        newInspectionSectionProperties[property] = newValue;
        this.saveInspectionSectionProperties({
          updateInspectionTemplate: this.inspectionTemplate,
          inspectionSectionId: this.inspectionSectionId,
          newInspectionSectionProperties: newInspectionSectionProperties,
          reason: `Edited section property ${this.inspectionTemplateId}/${property}`,
        });
      }
    },

    focus() {
      const ref = getRef(this.$refs, 'title');
      if (ref) {
        ref.focus();
      }
    },

    onItemMoved(e) {
      this.saveInspectionSectionItemProperties({
        updateInspectionTemplate: this.inspectionTemplate,
        inspectionSectionId: this.inspectionSectionId,
        itemIndex: e.oldIndex,
        newIndex: e.newIndex,
      });
    },
  },

  watch: {
    itemCount() {
      if (this.newItemAdded) {
        this.$nextTick(() => {
          const ref = getRef(this.$refs, 'new');
          if (ref) {
            ref.focus();
          }
        });
      }
      this.newItemAdded = true;
    },
  },
};
</script>

<style scoped>
.edit-inspection-section {
  background: rgba(0, 0, 0, 0.236);
  border: 1px solid white;
  border-radius: 5px;
  margin-bottom: 1cm;
}

.items {
  background: rgba(0, 0, 0, 0.236);
  border-top: 1px solid white;
  padding-left: 2cm;
}

img.icon {
  width: 25px;
  height: 25px;
  margin-right: 5px;
}
.boon-form-container {
  display: flex;
  flex-direction: row;
  padding: 20px 0;
  margin: 0 30px;
}

.glyphicon-move {
  color: #ffffff;
  width: inherit;
  margin: 3px;
}

.archived {
  color: grey;
}

.sortable-chosen {
  border: 1px solid #d1d463;
  box-shadow: 0px 0px 42px -6px #fffb85;
}
</style>



// WEBPACK FOOTER //
// src/components/project/EditInspectionTemplateSection.vue